import React, { useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Paper, Divider, Chip, Stack } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import backendApi from '../../services/backendApi';

// Simple JSON prettifier component
const JsonView = ({ src }: { src: any }) => {
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#272822',
        color: '#f8f8f2',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: '400px',
        fontFamily: 'monospace'
      }}
    >
      <pre>{JSON.stringify(src, null, 2)}</pre>
    </Box>
  );
};

const AdminCoinbasePage: React.FC = () => {
  const { state } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [detailedError, setDetailedError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [apiKeys, setApiKeys] = useState<any>(null);

  // Function to fetch raw Coinbase data
  const fetchRawCoinbaseData = async () => {
    setLoading(true);
    setError(null);
    setRawData(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to fetch raw Coinbase data');
        setLoading(false);
        return;
      }
      
      console.log('Fetching completely unmodified raw Coinbase data from Java API...');
      console.log('Fetching Coinbase raw data...');
      console.log('User email:', state.user.email);
      
      // Include the user's email as a parameter to identify which user's credentials to use
      const response = await backendApi.get<any>(`/api/coinbase/raw-data?email=${state.user.email}`);
      
      console.log('Raw Coinbase data response:', response.data);
      
      // Check if the response contains error information
      if (response.data && response.data.code && response.data.code !== 200) {
        // This is an error response from our backend
        console.log('Detected error response from backend:', response.data);
        
        // Set error state variables
        setError(response.data.message || 'Error from Coinbase API');
        setDetailedError(response.data.developerMessage || '');
        
        if (response.data.moreInformation && response.data.moreInformation.suggestion) {
          setSuggestion(response.data.moreInformation.suggestion);
        }
        
        // Still set the raw data so we can display the full error details
        setRawData(response.data);
      } else {
        // Normal success response
        setRawData(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching raw Coinbase data:', error);
      
      let errorMessage = 'Failed to fetch raw Coinbase data';
      let detailedError = '';
      let suggestion = '';
      
      if (error.response) {
        console.error('Error response:', error.response);
        // Handle the structured error response from our backend
        if (error.response.data) {
          const responseData = error.response.data;
          
          // Use the user-friendly message if available
          if (responseData.message) {
            errorMessage = responseData.message;
          }
          
          // Add the developer message as detailed error
          if (responseData.developerMessage) {
            detailedError = responseData.developerMessage.split('\n')[0]; // Get just the first line
          }
          
          // Add suggestion if available
          if (responseData.moreInformation && responseData.moreInformation.suggestion) {
            suggestion = responseData.moreInformation.suggestion;
          }
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
      setDetailedError(detailedError);
      setSuggestion(suggestion);
    } finally {
      setLoading(false);
    }
  };

  // Function to check Coinbase API health
  const checkCoinbaseApiHealth = async () => {
    setLoading(true);
    setError(null);
    setHealthStatus(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to check Coinbase API health');
        setLoading(false);
        return;
      }
      
      console.log('Checking Coinbase API health...');
      
      const response = await backendApi.get<any>(`/api/coinbase/health`);
      
      console.log('Health check response:', response);
      
      // Handle the response properly
      if (response && typeof response === 'object') {
        setHealthStatus(response);
      } else {
        console.error('Unexpected health check response format:', response);
        setError('Received an invalid response from the health check endpoint');
      }
    } catch (error: any) {
      console.error('Error checking Coinbase API health:', error);
      
      let errorMessage = 'Failed to check Coinbase API health';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to get Coinbase API keys
  const getCoinbaseApiKeys = async () => {
    setLoading(true);
    setError(null);
    setApiKeys(null);
    
    try {
      if (!state.user || !state.user.email) {
        setError('You must be logged in to get Coinbase API keys');
        setLoading(false);
        return;
      }
      
      console.log('Getting Coinbase API keys...');
      
      // Use the currently logged-in user's email
      const response = await backendApi.get<any>(`/api/coinbase/api-keys?email=${state.user.email}`);
      
      console.log('API keys response:', response);
      
      // Handle both success and error responses from the API
      if (response && typeof response === 'object') {
        setApiKeys(response);
        
        // If the response indicates an error, set the error message
        if (response.status === 'error') {
          setError(response.message || 'Failed to get Coinbase API keys');
        }
      } else {
        console.error('Unexpected API response format:', response);
        setError('Received an invalid response from the server');
      }
    } catch (error: any) {
      console.error('Error getting Coinbase API keys:', error);
      
      let errorMessage = 'Failed to get Coinbase API keys';
      
      if (error.response) {
        console.error('Error response:', error.response);
        if (error.response.data && error.response.data.message) {
          errorMessage = `${errorMessage}: ${error.response.data.message}`;
        }
      } else if (error.request) {
        errorMessage = `${errorMessage}: No response received (timeout or network issue)`;
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  if (!state.user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Admin Coinbase Page</Typography>
        <Alert severity="error">You must be logged in to view this page</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Coinbase Admin Dashboard</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {detailedError && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {detailedError}
              </Typography>
            </Box>
          )}
          {suggestion && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Suggestion: {suggestion}
              </Typography>
            </Box>
          )}
        </Alert>
      )}
      
      {rawData && rawData.error && (
        <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: '#331111', border: '1px solid #662222' }}>
          <Typography variant="h6" gutterBottom color="#ff5555" sx={{ fontWeight: 'bold' }}>Coinbase API Error Details</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" color="#ffffff" sx={{ fontWeight: 'medium' }}>{rawData.message}</Typography>
            {rawData.coinbaseErrorDetails && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#222222', borderRadius: 1, border: '1px solid #444444' }}>
                <Typography variant="subtitle2" gutterBottom color="#bbbbbb" sx={{ fontWeight: 'bold' }}>Error Details:</Typography>
                <Typography variant="body2" color="#dddddd" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {rawData.coinbaseErrorDetails}
                </Typography>
              </Box>
            )}
            {rawData.statusCode && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#222222', borderRadius: 1, border: '1px solid #444444' }}>
                <Typography variant="subtitle2" color="#bbbbbb" sx={{ fontWeight: 'medium' }}>HTTP Status: {rawData.statusCode}</Typography>
              </Box>
            )}
            {rawData.responseBody && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#222222', borderRadius: 1, border: '1px solid #444444' }}>
                <Typography variant="subtitle2" gutterBottom color="#bbbbbb" sx={{ fontWeight: 'bold' }}>Response Body:</Typography>
                <Typography variant="body2" color="#dddddd" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {rawData.responseBody}
                </Typography>
              </Box>
            )}
            {rawData.rawApiError && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#222222', borderRadius: 1, border: '1px solid #444444' }}>
                <Typography variant="subtitle2" gutterBottom color="#bbbbbb" sx={{ fontWeight: 'bold' }}>Raw API Error:</Typography>
                <Typography variant="body2" color="#dddddd" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {rawData.rawApiError}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
      
      <Alert severity="info" sx={{ mb: 2 }}>
        This page displays completely unmodified raw data from the Coinbase API for debugging purposes. All API calls use real credentials and real data - no mock data is used.
      </Alert>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={fetchRawCoinbaseData} 
          disabled={loading}
        >
          Get Raw Coinbase Data
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={checkCoinbaseApiHealth} 
          disabled={loading}
        >
          Check API Health
        </Button>
        <Button 
          variant="contained" 
          color="info" 
          onClick={getCoinbaseApiKeys} 
          disabled={loading}
        >
          Get API Keys
        </Button>

      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        This page displays completely unmodified raw data from the Coinbase API for debugging purposes.
      </Alert>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {rawData && (
        <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: '#333333', border: '1px solid #555555' }}>
          <Typography variant="h6" gutterBottom color="#ffffff" sx={{ fontWeight: 'medium' }}>Raw Coinbase Account Data</Typography>
          <JsonView src={rawData} />
        </Paper>
      )}
      
      {healthStatus && (
        <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: '#333333', border: '1px solid #555555' }}>
          <Typography variant="h6" gutterBottom color="#ffffff" sx={{ fontWeight: 'medium' }}>Coinbase API Health Status</Typography>
          <JsonView src={healthStatus} />
        </Paper>
      )}
      
      {apiKeys && (
        <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: '#333333', border: '1px solid #555555' }}>
          <Typography variant="h6" gutterBottom color="#ffffff" sx={{ fontWeight: 'medium' }}>Coinbase API Keys</Typography>
          <JsonView src={apiKeys} />
        </Paper>
      )}
      

    </Box>
  );
};

export default AdminCoinbasePage;
